/**
 * ScoreInfo — small "?" trigger with a popover explaining how Luma's
 * Q/C review scores work. Used beside the Library score badges and the
 * Dashboard "Comms health" stat.
 */
import { useEffect, useRef, useState } from "react";

export function ScoreInfo({ align = "left" }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <span className="beacon-score-info" ref={ref}>
      <button
        type="button"
        className="beacon-score-info-trigger"
        data-testid="score-info-trigger"
        aria-label="How Luma scores your packs"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        ?
      </button>
      {open && (
        <div
          className={`beacon-score-info-popover is-${align}`}
          data-testid="score-info-popover"
          role="dialog"
        >
          <div className="beacon-score-info-title">
            How Luma scores your packs
          </div>
          <p>
            Luma reads the full pack — toolbox talk, poster, email and
            intranet post — and scores it 1–10 on two axes:
          </p>
          <p>
            <strong>Q · Quality</strong> — tone, Aussie site vernacular,
            clarity and crew-readiness. Would this land at an actual
            toolbox talk?
          </p>
          <p>
            <strong>C · Compliance</strong> — jurisdiction framing (Model
            WHS vs Victorian OHS), correct regulator names and
            liability-safe wording.
          </p>
          <p>
            <span className="beacon-score-info-dot is-good" /> 8+ solid
            &nbsp;·&nbsp;
            <span className="beacon-score-info-dot is-ok" /> 6–7 needs a
            polish &nbsp;·&nbsp;
            <span className="beacon-score-info-dot is-poor" /> below 6
            worth a rebuild
          </p>
          <p className="beacon-score-info-note">
            Scores are Luma&rsquo;s AI judgement — a directional health
            signal, not an audit. Re-reviews can shift a point either
            way. Use <strong>Rebuild&nbsp;→</strong> on a review to lift
            a low score; <strong>Comms health</strong> on the Dashboard
            is the average across all reviewed packs.
          </p>
          <button
            type="button"
            className="beacon-score-info-more"
            data-testid="score-info-full-guide"
            onClick={() => {
              setOpen(false);
              window.dispatchEvent(new Event("beacon-open-help"));
            }}
          >
            Read the full guide →
          </button>
        </div>
      )}
    </span>
  );
}
